import {
    FlagOverride,
    EnvironmentFlags,
    DataChannelOverride,
    Pipeline,
    Region,
    WorkLane,
} from '../models/EnvironmentModels';
import { ReleasePipelineSource } from '../../AGSS/models/ReleasePipelineSource';
export { downloadCsv } from '../../Common/util/downloadCsv';

// ─── Feature Flag keys (stable column order) ───
const FLAG_KEYS: (keyof EnvironmentFlags)[] = [
    'useDataChannelOverrides',
    'cachingEnabled',
    'messagingEnabled',
    'storageUploadEnabled',
    'octopusEnabled',
    'lockBoxEnabled',
    'egressEnabled',
    'icmEnabled',
    'requiredOnNetworkShare',
    'useDynamicHealthChecks',
    'useSrxV2',
    'blockSrxExecution',
    'messagingHeartbeatEnabled',
];

// ─── CSV helpers ───

/** Escape a value for CSV: wrap in quotes if it contains comma, quote, or newline */
function csvEscape(value: string): string {
    if (
        value.includes(',') ||
        value.includes('"') ||
        value.includes('\n') ||
        value.includes('\r')
    ) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

/** Convert a value to a CSV-safe string */
function toCsvValue(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return csvEscape(String(value));
}

/** Parse a string cell back to boolean | null */
function parseBoolOrNull(cell: string): boolean | null {
    const trimmed = cell.trim().toLowerCase();
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    return null;
}

/** Parse a nullable string cell */
function parseNullableString(cell: string): string | null {
    const trimmed = cell.trim();
    return trimmed === '' ? null : trimmed;
}

/** Parse tags from semicolon-delimited string */
function parseTags(cell: string): string[] {
    if (!cell || cell.trim() === '') return [];
    return cell
        .split(';')
        .map((t) => t.trim())
        .filter((t) => t !== '');
}

/** Parse ReleasePipelineSource from string */
function parsePipelineSourceType(cell: string): ReleasePipelineSource | null {
    const trimmed = cell.trim().toLowerCase();
    if (trimmed === 'classic' || trimmed === '0')
        return ReleasePipelineSource.Classic;
    if (trimmed === 'yaml' || trimmed === '1')
        return ReleasePipelineSource.YAML;
    if (trimmed === 'generic' || trimmed === '2')
        return ReleasePipelineSource.Generic;
    return null;
}

/** Format a ReleasePipelineSource to display string */
function formatPipelineSourceType(
    value: ReleasePipelineSource | null | undefined
): string {
    if (value === null || value === undefined) return '';
    switch (value) {
        case ReleasePipelineSource.Classic:
            return 'Classic';
        case ReleasePipelineSource.YAML:
            return 'YAML';
        case ReleasePipelineSource.Generic:
            return 'Generic';
        default:
            return '';
    }
}

// ═══════════════════════════════════════════════════════
// FeatureFlagOverride CSV
// ═══════════════════════════════════════════════════════

const FLAG_OVERRIDE_COLUMNS = [
    'name',
    'enabled',
    'active',
    'serviceTreeId',
    'accountName',
    'projectName',
    'branchName',
    'releaseDefinitionId',
    'pipelineSourceType',
    'tags',
    'startTime',
    'endTime',
    ...FLAG_KEYS,
];

/** Export FlagOverride[] to CSV string */
export function exportFlagOverridesToCsv(overrides: FlagOverride[]): string {
    const rows: string[] = [];

    // Header row
    rows.push(FLAG_OVERRIDE_COLUMNS.join(','));

    for (const o of overrides) {
        const cells: string[] = [
            toCsvValue(o.name),
            toCsvValue(o.enabled),
            toCsvValue(o.active),
            toCsvValue(o.serviceTreeId ?? null),
            toCsvValue(o.accountName ?? null),
            toCsvValue(o.projectName ?? null),
            toCsvValue(o.branchName ?? null),
            toCsvValue(o.releaseDefinitionId ?? null),
            toCsvValue(formatPipelineSourceType(o.pipelineSourceType)),
            toCsvValue((o.tags ?? []).join(';')),
            toCsvValue(o.startTime ?? null),
            toCsvValue(o.endTime ?? null),
            // Feature flag columns
            ...FLAG_KEYS.map((key) => {
                const val = o.featureFlags?.[key];
                if (val === null || val === undefined) return '';
                return val ? 'true' : 'false';
            }),
        ];
        rows.push(cells.join(','));
    }

    return rows.join('\n');
}

/** Generate a timestamped filename */
export function generateCsvFilename(prefix: string): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
    )}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(
        now.getSeconds()
    )}`;
    return `${prefix}_${timestamp}.csv`;
}

// ─── CSV Parsing ───

/** Parse CSV text into rows of string arrays, respecting quoted fields */
function parseCsvText(text: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = '';
    let inQuotes = false;
    let i = 0;

    while (i < text.length) {
        const ch = text[i];

        if (inQuotes) {
            if (ch === '"') {
                if (i + 1 < text.length && text[i + 1] === '"') {
                    currentCell += '"';
                    i += 2;
                } else {
                    inQuotes = false;
                    i++;
                }
            } else {
                currentCell += ch;
                i++;
            }
        } else {
            if (ch === '"') {
                inQuotes = true;
                i++;
            } else if (ch === ',') {
                currentRow.push(currentCell);
                currentCell = '';
                i++;
            } else if (ch === '\r') {
                // Handle \r\n or standalone \r
                currentRow.push(currentCell);
                currentCell = '';
                rows.push(currentRow);
                currentRow = [];
                if (i + 1 < text.length && text[i + 1] === '\n') {
                    i += 2;
                } else {
                    i++;
                }
            } else if (ch === '\n') {
                currentRow.push(currentCell);
                currentCell = '';
                rows.push(currentRow);
                currentRow = [];
                i++;
            } else {
                currentCell += ch;
                i++;
            }
        }
    }

    // Final cell/row
    if (currentCell !== '' || currentRow.length > 0) {
        currentRow.push(currentCell);
        rows.push(currentRow);
    }

    return rows;
}

// ─── MessageId extraction (dgrep / Kusto CSV export) ───

export interface MessageIdCsvParseResult {
    /** Distinct, non-empty MessageId values in the order encountered. */
    ids: string[];
    /** Count of non-empty MessageId cells found (before de-duplication). */
    totalRows: number;
    /** Count of duplicate MessageId values that were removed. */
    duplicatesRemoved: number;
    /** Populated when the CSV could not be parsed into a usable id list. */
    error?: string;
}

// Header names that may hold the message id, in priority order.
const MESSAGE_ID_COLUMN_CANDIDATES = ['messageid', 'message_id'];

/**
 * Parse a dgrep / Kusto CSV export and extract the distinct MessageId values.
 * Looks for a "MessageId" column (case-insensitive) and returns a de-duplicated
 * list suitable for the replayByIds endpoint.
 */
export function parseMessageIdsFromCsv(
    csvText: string
): MessageIdCsvParseResult {
    const result: MessageIdCsvParseResult = {
        ids: [],
        totalRows: 0,
        duplicatesRemoved: 0,
    };

    // Drop fully-empty rows (trailing newline produces one).
    const rows = parseCsvText(csvText).filter(
        (r) => !(r.length === 1 && r[0].trim() === '')
    );

    if (rows.length < 2) {
        result.error = 'CSV must have a header row and at least one data row.';
        return result;
    }

    const header = rows[0].map((h) => h.trim().toLowerCase());

    let columnIndex = -1;
    for (const candidate of MESSAGE_ID_COLUMN_CANDIDATES) {
        columnIndex = header.indexOf(candidate);
        if (columnIndex !== -1) {
            break;
        }
    }

    if (columnIndex === -1) {
        result.error =
            'No "MessageId" column found in the CSV header. Include the MessageId field in the dgrep query export.';
        return result;
    }

    const seen = new Set<string>();
    for (let i = 1; i < rows.length; i++) {
        const id = (rows[i][columnIndex] ?? '').trim();
        if (id === '') {
            continue;
        }

        result.totalRows++;

        if (seen.has(id)) {
            result.duplicatesRemoved++;
            continue;
        }

        seen.add(id);
        result.ids.push(id);
    }

    if (result.ids.length === 0) {
        result.error = 'No MessageId values were found in the CSV.';
    }

    return result;
}

/**
 * Parse a free-form, pasted list of message ids. Accepts ids separated by
 * newlines, commas, spaces, semicolons, or tabs, and tolerates JSON-array
 * style input such as ["123","456"] (surrounding brackets and quotes are
 * stripped). Returns a de-duplicated list in the order encountered.
 */
export function parseMessageIdsFromText(text: string): MessageIdCsvParseResult {
    const result: MessageIdCsvParseResult = {
        ids: [],
        totalRows: 0,
        duplicatesRemoved: 0,
    };

    if (!text || text.trim() === '') {
        result.error = 'Enter one or more message ids.';
        return result;
    }

    // Split on common delimiters, then strip surrounding brackets/quotes/whitespace.
    const tokens = text
        .split(/[\s,;]+/)
        .map((t) => t.replace(/^[[\]"']+|[[\]"']+$/g, '').trim())
        .filter((t) => t !== '');

    const seen = new Set<string>();
    for (const id of tokens) {
        result.totalRows++;

        if (seen.has(id)) {
            result.duplicatesRemoved++;
            continue;
        }

        seen.add(id);
        result.ids.push(id);
    }

    if (result.ids.length === 0) {
        result.error = 'No message ids were found in the pasted text.';
    }

    return result;
}

// ─── Validation result types ───

export interface CsvValidationError {
    row: number; // 1-based (header = 1)
    column: string;
    message: string;
}

export interface FlagOverrideCsvImportResult {
    overrides: FlagOverride[];
    errors: CsvValidationError[];
    totalRows: number;
    validRows: number;
    skippedRows: number;
}

/** Parse and validate a CSV string into FlagOverride[] */
export function parseFlagOverridesCsv(
    csvText: string
): FlagOverrideCsvImportResult {
    const rows = parseCsvText(csvText);
    const result: FlagOverrideCsvImportResult = {
        overrides: [],
        errors: [],
        totalRows: 0,
        validRows: 0,
        skippedRows: 0,
    };

    if (rows.length < 2) {
        result.errors.push({
            row: 0,
            column: '',
            message: 'CSV must have a header row and at least one data row',
        });
        return result;
    }

    // Parse header
    const header = rows[0].map((h) => h.trim().toLowerCase());
    result.totalRows = rows.length - 1;

    // Build column index map
    const colIndex = (name: string): number => {
        return header.indexOf(name.toLowerCase());
    };

    // Validate required columns
    const requiredColumns = ['name', 'enabled'];
    for (const col of requiredColumns) {
        if (colIndex(col) === -1) {
            result.errors.push({
                row: 1,
                column: col,
                message: `Required column "${col}" is missing from the header`,
            });
        }
    }

    // Reject if data channel columns are present (wrong override type)
    const channelColumns = ['pipeline', 'region', 'worklane'];
    const channelColumnsFound = channelColumns.filter(
        (key) => colIndex(key) !== -1
    );
    if (
        channelColumnsFound.length > 0 &&
        FLAG_KEYS.every((key) => colIndex(key) === -1)
    ) {
        result.errors.push({
            row: 1,
            column: channelColumnsFound[0],
            message: `This CSV contains data channel columns (${channelColumnsFound.join(
                ', '
            )}) but no feature flag columns. Use the Data Channel Overrides import instead.`,
        });
        return result;
    }

    if (result.errors.length > 0) {
        return result;
    }

    // Parse data rows
    for (let r = 1; r < rows.length; r++) {
        const cells = rows[r];
        // Skip completely empty rows
        if (cells.every((c) => c.trim() === '')) {
            result.skippedRows++;
            continue;
        }

        const getCell = (colName: string): string => {
            const idx = colIndex(colName);
            if (idx === -1 || idx >= cells.length) return '';
            return cells[idx].trim();
        };

        const rowErrors: CsvValidationError[] = [];

        // Validate name
        const name = getCell('name');
        if (!name) {
            rowErrors.push({
                row: r + 1,
                column: 'name',
                message: 'Name is required',
            });
        }

        // Validate enabled
        const enabledStr = getCell('enabled').toLowerCase();
        if (enabledStr !== 'true' && enabledStr !== 'false') {
            rowErrors.push({
                row: r + 1,
                column: 'enabled',
                message: `Invalid enabled value: "${getCell(
                    'enabled'
                )}". Must be "true" or "false"`,
            });
        }

        // Validate pipelineSourceType if present
        const pstCell = getCell('pipelinesourcetype');
        if (
            pstCell &&
            !['classic', 'yaml', 'generic', '0', '1', '2'].includes(
                pstCell.toLowerCase()
            )
        ) {
            rowErrors.push({
                row: r + 1,
                column: 'pipelineSourceType',
                message: `Invalid pipelineSourceType: "${pstCell}". Must be Classic, YAML, or Generic`,
            });
        }

        // Validate feature flag values
        for (const flagKey of FLAG_KEYS) {
            const flagCell = getCell(flagKey);
            if (
                flagCell &&
                !['true', 'false'].includes(flagCell.toLowerCase())
            ) {
                rowErrors.push({
                    row: r + 1,
                    column: flagKey,
                    message: `Invalid flag value for ${flagKey}: "${flagCell}". Must be "true", "false", or empty (not set)`,
                });
            }
        }

        if (rowErrors.length > 0) {
            result.errors.push(...rowErrors);
            result.skippedRows++;
            continue;
        }

        // Build the FlagOverride
        const featureFlags: EnvironmentFlags = {};
        for (const flagKey of FLAG_KEYS) {
            featureFlags[flagKey] = parseBoolOrNull(getCell(flagKey));
        }

        const override: FlagOverride = {
            name: name,
            enabled: enabledStr === 'true',
            active: parseBoolOrNull(getCell('active')) ?? false,
            serviceTreeId: parseNullableString(getCell('servicetreeid')),
            accountName: parseNullableString(getCell('accountname')),
            projectName: parseNullableString(getCell('projectname')),
            branchName: parseNullableString(getCell('branchname')),
            releaseDefinitionId: parseNullableString(
                getCell('releasedefinitionid')
            ),
            pipelineSourceType: parsePipelineSourceType(
                getCell('pipelinesourcetype')
            ),
            tags: parseTags(getCell('tags')),
            startTime: parseNullableString(getCell('starttime')),
            endTime: parseNullableString(getCell('endtime')),
            featureFlags,
        };

        result.overrides.push(override);
        result.validRows++;
    }

    return result;
}

// ═══════════════════════════════════════════════════════
// DataChannelOverride CSV
// ═══════════════════════════════════════════════════════

const DATA_CHANNEL_COLUMNS = [
    'name',
    'enabled',
    'active',
    'serviceTreeId',
    'accountName',
    'projectName',
    'branchName',
    'releaseDefinitionId',
    'pipelineSourceType',
    'tags',
    'startTime',
    'endTime',
    'pipeline',
    'region',
    'workLane',
];

function formatPipeline(value: Pipeline | null | undefined): string {
    if (value === null || value === undefined) return '';
    switch (value) {
        case Pipeline.Blob:
            return 'Blob';
        case Pipeline.Core:
            return 'Core';
        case Pipeline.Message:
            return 'Message';
        default:
            return '';
    }
}

function parsePipeline(cell: string): Pipeline | null {
    const trimmed = cell.trim().toLowerCase();
    if (trimmed === 'blob' || trimmed === '0') return Pipeline.Blob;
    if (trimmed === 'core' || trimmed === '1') return Pipeline.Core;
    if (trimmed === 'message' || trimmed === '2') return Pipeline.Message;
    return null;
}

function formatRegion(value: Region | null | undefined): string {
    if (value === null || value === undefined) return '';
    switch (value) {
        case Region.Primary:
            return 'Primary';
        case Region.Secondary:
            return 'Secondary';
        default:
            return '';
    }
}

function parseRegion(cell: string): Region | null {
    const trimmed = cell.trim().toLowerCase();
    if (trimmed === 'primary' || trimmed === '0') return Region.Primary;
    if (trimmed === 'secondary' || trimmed === '1') return Region.Secondary;
    return null;
}

function formatWorkLane(value: WorkLane | null | undefined): string {
    if (value === null || value === undefined) return '';
    switch (value) {
        case WorkLane.Warm:
            return 'Warm';
        case WorkLane.Dcmt:
            return 'Dcmt';
        default:
            return '';
    }
}

function parseWorkLane(cell: string): WorkLane | null {
    const trimmed = cell.trim().toLowerCase();
    if (trimmed === 'warm' || trimmed === '0') return WorkLane.Warm;
    if (trimmed === 'dcmt' || trimmed === '1') return WorkLane.Dcmt;
    return null;
}

/** Export DataChannelOverride[] to CSV string */
export function exportDataChannelOverridesToCsv(
    overrides: DataChannelOverride[]
): string {
    const rows: string[] = [];
    rows.push(DATA_CHANNEL_COLUMNS.join(','));

    for (const o of overrides) {
        const cells: string[] = [
            toCsvValue(o.name),
            toCsvValue(o.enabled),
            toCsvValue(o.active),
            toCsvValue(o.serviceTreeId ?? null),
            toCsvValue(o.accountName ?? null),
            toCsvValue(o.projectName ?? null),
            toCsvValue(o.branchName ?? null),
            toCsvValue(o.releaseDefinitionId ?? null),
            toCsvValue(formatPipelineSourceType(o.pipelineSourceType)),
            toCsvValue((o.tags ?? []).join(';')),
            toCsvValue(o.startTime ?? null),
            toCsvValue(o.endTime ?? null),
            toCsvValue(formatPipeline(o.pipeline)),
            toCsvValue(formatRegion(o.region)),
            toCsvValue(formatWorkLane(o.workLane)),
        ];
        rows.push(cells.join(','));
    }

    return rows.join('\n');
}

export interface DataChannelCsvImportResult {
    overrides: DataChannelOverride[];
    errors: CsvValidationError[];
    totalRows: number;
    validRows: number;
    skippedRows: number;
}

/** Parse and validate a CSV string into DataChannelOverride[] */
export function parseDataChannelOverridesCsv(
    csvText: string
): DataChannelCsvImportResult {
    const rows = parseCsvText(csvText);
    const result: DataChannelCsvImportResult = {
        overrides: [],
        errors: [],
        totalRows: 0,
        validRows: 0,
        skippedRows: 0,
    };

    if (rows.length < 2) {
        result.errors.push({
            row: 0,
            column: '',
            message: 'CSV must have a header row and at least one data row',
        });
        return result;
    }

    const header = rows[0].map((h) => h.trim().toLowerCase());
    result.totalRows = rows.length - 1;

    const colIndex = (name: string): number =>
        header.indexOf(name.toLowerCase());

    // Validate required columns
    const requiredColumns = ['name', 'enabled'];
    for (const col of requiredColumns) {
        if (colIndex(col) === -1) {
            result.errors.push({
                row: 1,
                column: col,
                message: `Required column "${col}" is missing from the header`,
            });
        }
    }

    // Reject if any feature flag columns are present (wrong override type)
    const flagColumnsFound = FLAG_KEYS.filter((key) => colIndex(key) !== -1);
    if (flagColumnsFound.length > 0) {
        result.errors.push({
            row: 1,
            column: flagColumnsFound[0],
            message: `This CSV contains feature flag columns (${flagColumnsFound.join(
                ', '
            )}). Use the Feature Flag Overrides import instead.`,
        });
        return result;
    }

    if (result.errors.length > 0) {
        return result;
    }

    for (let r = 1; r < rows.length; r++) {
        const cells = rows[r];
        if (cells.every((c) => c.trim() === '')) {
            result.skippedRows++;
            continue;
        }

        const getCell = (colName: string): string => {
            const idx = colIndex(colName);
            if (idx === -1 || idx >= cells.length) return '';
            return cells[idx].trim();
        };

        const rowErrors: CsvValidationError[] = [];

        const name = getCell('name');
        if (!name) {
            rowErrors.push({
                row: r + 1,
                column: 'name',
                message: 'Name is required',
            });
        }

        const enabledStr = getCell('enabled').toLowerCase();
        if (enabledStr !== 'true' && enabledStr !== 'false') {
            rowErrors.push({
                row: r + 1,
                column: 'enabled',
                message: `Invalid enabled value: "${getCell(
                    'enabled'
                )}". Must be "true" or "false"`,
            });
        }

        const pstCell = getCell('pipelinesourcetype');
        if (
            pstCell &&
            !['classic', 'yaml', 'generic', '0', '1', '2'].includes(
                pstCell.toLowerCase()
            )
        ) {
            rowErrors.push({
                row: r + 1,
                column: 'pipelineSourceType',
                message: `Invalid pipelineSourceType: "${pstCell}". Must be Classic, YAML, or Generic`,
            });
        }

        const pipelineCell = getCell('pipeline');
        if (
            pipelineCell &&
            !['blob', 'core', 'message', '0', '1', '2'].includes(
                pipelineCell.toLowerCase()
            )
        ) {
            rowErrors.push({
                row: r + 1,
                column: 'pipeline',
                message: `Invalid pipeline: "${pipelineCell}". Must be Blob, Core, or Message`,
            });
        }

        const regionCell = getCell('region');
        if (
            regionCell &&
            !['primary', 'secondary', '0', '1'].includes(
                regionCell.toLowerCase()
            )
        ) {
            rowErrors.push({
                row: r + 1,
                column: 'region',
                message: `Invalid region: "${regionCell}". Must be Primary or Secondary`,
            });
        }

        const workLaneCell = getCell('worklane');
        if (
            workLaneCell &&
            !['warm', 'dcmt', '0', '1'].includes(workLaneCell.toLowerCase())
        ) {
            rowErrors.push({
                row: r + 1,
                column: 'workLane',
                message: `Invalid workLane: "${workLaneCell}". Must be Warm or Dcmt`,
            });
        }

        if (rowErrors.length > 0) {
            result.errors.push(...rowErrors);
            result.skippedRows++;
            continue;
        }

        const override: DataChannelOverride = {
            name: name,
            enabled: enabledStr === 'true',
            active: parseBoolOrNull(getCell('active')) ?? false,
            serviceTreeId: parseNullableString(getCell('servicetreeid')),
            accountName: parseNullableString(getCell('accountname')),
            projectName: parseNullableString(getCell('projectname')),
            branchName: parseNullableString(getCell('branchname')),
            releaseDefinitionId: parseNullableString(
                getCell('releasedefinitionid')
            ),
            pipelineSourceType: parsePipelineSourceType(
                getCell('pipelinesourcetype')
            ),
            tags: parseTags(getCell('tags')),
            startTime: parseNullableString(getCell('starttime')),
            endTime: parseNullableString(getCell('endtime')),
            pipeline: parsePipeline(getCell('pipeline')),
            region: parseRegion(getCell('region')),
            workLane: parseWorkLane(getCell('worklane')),
        };

        result.overrides.push(override);
        result.validRows++;
    }

    return result;
}
